/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import { LitElement, css, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { attachHls, type HlsHandle } from "../services/hls";
import { hlsUrl } from "../services/config";
import { getLogos, LOGO_SCALE_DEFAULT } from "../services/sessions";
import "./rk-overlay";

/**
 * <rk-video logger-id="..." primary="front|rear" pip="on|off"
 *           muted="true|false" can-fullscreen show-controls>
 *
 * Dual-camera video player. Front + rear streams always mounted; one is
 * primary (full-bleed), the other is PiP. Audio is always wired to the FRONT
 * stream regardless of which is primary. PiP can be dragged on desktop.
 *
 * Stall detection + soft reload is delegated to the hls.js wrapper. We surface
 * the "connecting" overlay when the primary video stalls past a threshold.
 */

type Cam = "front" | "rear";

const CONNECTING_DELAY_MS = 1800;
const OFFLINE_MSG_MS = 15_000;

/** How long the inset may go without new frames before it is hidden.
 *
 *  Deliberately slow to hide and instant to restore. This triggers precisely
 *  when a link is marginal, and an inset that blinks in and out every few
 *  seconds is worse than one that sits black — so a drop must be sustained
 *  before we act, while a recovery shows up on the next check. */
const PIP_HIDE_AFTER_MS = 20_000;

@customElement("rk-video")
export class RkVideo extends LitElement {
  static styles = css`
    :host {
      position: relative;
      display: block;
      width: 100%;
      height: 100%;
      background: #000;
      overflow: hidden;
      border-radius: var(--rk-r-lg);
      isolation: isolate;

      /* Per-car client-logo size, overwritten inline once the row is fetched.
         Declared here so every calc() below always resolves: an unresolved
         var() invalidates the whole declaration, and a dropped height on an
         <img> means it draws at its natural pixel size — a logo filling the
         video is a far worse failure than one that is the wrong size. */
      --rk-logo-scale: 2;
    }

    .stage {
      position: absolute;
      inset: 0;
      /* Lets the overlays size themselves against the VIDEO's width rather than
         the viewport, which matters because the details panel changes how wide
         the video actually is at the same window size. */
      container-type: inline-size;
    }

    /* RaceKeeper logo — overlays the top-left of the video itself
       (not in the page header). Matches v2's .rk-stageLogo. */
    .stage-logo {
      position: absolute;
      left: calc(14px + env(safe-area-inset-left));
      top: calc(14px + env(safe-area-inset-top));
      z-index: 6;
      pointer-events: none;
      filter: drop-shadow(0 4px 14px rgba(0, 0, 0, 0.55));
    }
    .stage-logo img {
      height: 32px;
      width: auto;
      max-width: 160px;
      object-fit: contain;
      display: block;
    }
    @media (max-width: 720px),
           (orientation: landscape) and (max-height: 500px) {
      .stage-logo { left: 10px; top: max(10px, env(safe-area-inset-top)); }
      .stage-logo img { height: 24px; max-width: 120px; }
    }

    /* Client / sponsor logos — top centre, alongside the Race-Keeper mark which
       stays top left. Sized by HEIGHT only with width:auto so the stored aspect
       ratio is never altered. Never interactive, so it cannot swallow a click
       meant for the video. The same drop shadow as the RK logo keeps white
       wordmarks readable against bright sky or barriers. */
    .client-logos {
      position: absolute;
      top: calc(14px + env(safe-area-inset-top));
      left: 50%;
      transform: translateX(-50%);
      z-index: 6;
      pointer-events: none;
      display: flex;
      align-items: center;
      gap: calc(14px * var(--rk-logo-scale));
      /* A hard geometric limit, not a taste one: the strip is centred and the
         rear PiP starts at 70% of the stage, so a centred strip wider than 40%
         reaches under it — 38% leaves a small gutter. This was 46%, which let
         three full-width wordmarks run beneath the PiP; the cap only binds in
         that case, so tightening it changes nothing for the one- and two-logo
         cars that are what actually run. The absolute cap grows with the scale
         so that on a large monitor the setting is what limits the size, not a
         px ceiling chosen for the old default. */
      max-width: min(38%, calc(520px * var(--rk-logo-scale)));
      filter: drop-shadow(0 4px 14px rgba(0, 0, 0, 0.6));
    }
    .client-logos img {
      /* Scales with the video's own width so the logos stay the same relative
         size on a laptop and on a large monitor. A fixed height made them look
         progressively smaller the bigger the screen got, because the video and
         the PiP grow but the logo did not. The plain px value is the fallback
         for browsers without container query support.

         --rk-logo-scale is the per-car size set from the console. Because the
         height alone is scaled and the width stays auto, the stored aspect
         ratio is never altered. If a wide pair of logos would exceed the cap
         above, flex shrinks them and object-fit keeps them undistorted — they
         simply end up smaller than the setting asked for rather than
         overlapping the PiP. */
      height: calc(30px * var(--rk-logo-scale));
      height: clamp(
        calc(26px * var(--rk-logo-scale)),
        calc(3.4cqw * var(--rk-logo-scale)),
        calc(72px * var(--rk-logo-scale))
      );
      width: auto;
      object-fit: contain;
      display: block;
      min-width: 0;
    }
    @media (max-width: 720px),
           (orientation: landscape) and (max-height: 500px) {
      .client-logos {
        top: max(10px, env(safe-area-inset-top));
        gap: 9px;
        max-width: 44%;
      }
      .client-logos img { height: calc(20px * var(--rk-logo-scale)); }
    }
    /* Portrait on a phone is the tight case: the rear PiP fills the top right,
       so centring on the video pushes the logos underneath it. Sit centred in
       the clear space between the Race-Keeper mark and the PiP instead, and cap
       the width so a third logo grows leftwards rather than into the PiP. */
    @media (orientation: portrait) and (max-width: 720px) {
      .client-logos {
        left: 41%;
        max-width: 33%;
        gap: 7px;
      }
      /* Two full wordmarks (the real two-sponsor case) only just fit the gap at
         20px, leaving them almost touching the Race-Keeper mark. Slightly
         shorter here buys proper breathing room on both sides and keeps the
         Race-Keeper mark visually primary. */
      .client-logos img { height: calc(17px * var(--rk-logo-scale)); }
    }

    video.primary {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      background: #000;
      transition: opacity var(--rk-dur-base) var(--rk-ease-out);
    }

    .pip {
      position: absolute;
      top: var(--rk-s-4);
      right: var(--rk-s-4);
      width: 28%;
      max-width: 280px;
      min-width: 140px;
      aspect-ratio: 16 / 9;
      border-radius: var(--rk-r-md);
      overflow: hidden;
      border: 1px solid var(--rk-border-strong);
      box-shadow: var(--rk-shadow-lg);
      background: #000;
      cursor: grab;
      transition: transform var(--rk-dur-fast) var(--rk-ease-out),
        box-shadow var(--rk-dur-fast) var(--rk-ease-out);
      z-index: 3;
      user-select: none;
      touch-action: none;
    }
    .pip:hover { transform: scale(1.02); box-shadow: var(--rk-shadow-glow); }
    .pip:active { cursor: grabbing; }
    .pip[hidden] { display: none; }
    .pip video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .pip-badge {
      position: absolute;
      bottom: 6px;
      left: 8px;
      padding: 2px 8px;
      font-family: var(--rk-font-mono);
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--rk-text);
      background: rgba(0, 0, 0, 0.55);
      backdrop-filter: blur(8px);
      border-radius: var(--rk-r-pill);
    }

    /* Top-left cluster on the PRIMARY video: the camera badge (so the user
       can see which feed is main after a swap) plus an always-visible
       mute/unmute toggle. The cluster sits far enough from the logo to not
       collide. It is the only reliably-reachable mute control on mobile,
       where the <rk-player> bottom-bar controls are hidden behind the
       collapsed details panel. */
    .cam-cluster {
      position: absolute;
      top: 56px;
      left: 16px;
      z-index: 4;
      display: flex;
      align-items: center;
      gap: 8px;
      /* Cluster itself is click-through; only the mute button opts back in,
         so a tap on the badge still falls through to the video (swap). */
      pointer-events: none;
    }
    .primary-badge {
      padding: 3px 10px;
      font-family: var(--rk-font-mono);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--rk-text);
      background: rgba(0, 0, 0, 0.55);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.18);
      border-radius: var(--rk-r-pill);
    }
    .mute-btn {
      pointer-events: auto;
      /* Hidden on desktop — <rk-player>'s bottom-bar already has a mute
         button there, so showing this too would be a duplicate. It is
         revealed only at the mobile breakpoint below, where the bottom-bar
         controls move into the collapsed details panel and are unreachable. */
      display: none;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      padding: 0;
      color: var(--rk-text);
      background: rgba(0, 0, 0, 0.55);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.18);
      border-radius: var(--rk-r-pill);
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      transition: background 0.15s ease, border-color 0.15s ease;
    }
    .mute-btn:hover { background: rgba(0, 0, 0, 0.72); }
    /* Unmuted = audio on: highlight so the state is obvious at a glance. */
    .mute-btn[aria-pressed="false"] {
      border-color: var(--rk-accent, rgba(255, 255, 255, 0.55));
    }
    .mute-btn svg { width: 16px; height: 16px; display: block; }
    @media (max-width: 720px),
           (orientation: landscape) and (max-height: 500px) {
      .cam-cluster { top: 44px; left: 12px; gap: 6px; }
      .primary-badge { font-size: 9px; }
      /* Mobile / short-landscape only: this is the sole reachable mute. */
      .mute-btn { display: inline-flex; }
    }

    /* Controls were moved out into <rk-player> so they sit on the same DOM
       layer as the HUD overlay and can flank it without being trapped
       inside the video's stacking context. */

    rk-overlay { pointer-events: none; }
    rk-overlay[state="connecting"],
    rk-overlay[state="offline"] { pointer-events: auto; }

    /* Front-camera HLS now carries an audio track again (re-encoded server
       side). Mute defaults ON so autoplay is allowed; the .mute-btn in the
       cam-cluster lets the viewer unmute with one tap. */

    @media (max-width: 720px),
           (orientation: landscape) and (max-height: 500px) {
      /* kvs20 PiP sizing: 34% of stage width, clamped 140–220px.
         The 220px cap matters on landscape phones (~844px wide) where
         34% would otherwise be ~287px and cover too much of the main
         video. aspect-ratio: 16/9 is inherited from the default block. */
      .pip {
        width: 34%;
        min-width: 140px;
        max-width: 220px;
      }
      :host { border-radius: 0; }
    }
  `;

  @property({ attribute: "logger-id" }) loggerId = "";
  @property() primary: Cam = "front";
  @property({ type: Boolean, reflect: true }) muted = true;
  @property({ type: Boolean, attribute: "pip-on" }) pipOn = true;

  @state() private overlayState: "hidden" | "connecting" | "offline" = "connecting";
  @state() private clientLogos: string[] = [];
  /** False once the inset camera has gone quiet for PIP_HIDE_AFTER_MS.
   *  Starts true so a normal startup, where HLS takes a few seconds to
   *  deliver the first frame, never flashes the inset away. */
  @state() private pipHasVideo = true;
  /** Overlay size multiplier for this car, from its provisioning row. */
  @state() private clientLogoScale = LOGO_SCALE_DEFAULT;

  @query("#video-primary") private vPrimary!: HTMLVideoElement;
  @query("#video-pip") private vPip!: HTMLVideoElement;
  @query(".pip") private pipEl!: HTMLDivElement;

  private hlsFront: HlsHandle | null = null;
  private hlsRear: HlsHandle | null = null;
  private stallSince: number | null = null;
  /** Last time the inset video was seen advancing; drives pipHasVideo. */
  private pipProgressAt = 0;
  private pipLastTime = -1;
  private overlayTimer: number | null = null;

  // Drag state for PiP
  private dragOff = { x: 0, y: 0 };
  private dragging = false;
  /** True once the PiP has actually moved during a pointer gesture —
   *  distinguishes a real drag (no swap on release) from a tap (swap). */
  private pipMoved = false;

  protected firstUpdated(): void {
    this.mountStreams();
    this.startOverlayWatchdog();
    this.loadClientLogos();
  }

  /** Pull this car's client logos. Deliberately fire-and-forget and failure
   *  tolerant: the overlay is decoration, so nothing here may delay or disturb
   *  playback. An empty result renders nothing at all. */
  private loadClientLogos(): void {
    const id = this.loggerId;
    if (!id) {
      this.clientLogos = [];
      return;
    }
    void getLogos(id)
      .then(({ logos, scale }) => {
        if (this.loggerId !== id) return; // ignore a stale car switch
        this.clientLogos = logos;
        this.clientLogoScale = scale;
      })
      .catch(() => {
        this.clientLogos = [];
      });
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has("loggerId")) {
      this.tearDown();
      this.mountStreams();
      this.clientLogos = [];
      this.clientLogoScale = LOGO_SCALE_DEFAULT;
      this.loadClientLogos();
      // A new car starts from scratch: assume it has an inset until proven
      // otherwise, or switching cars would inherit the old one's verdict.
      this.pipHasVideo = true;
      this.pipProgressAt = performance.now();
      this.pipLastTime = -1;
    }
    if (changed.has("primary")) {
      this.applyPrimary();
      // The inset now holds the other camera, so its progress history is
      // meaningless. Stay optimistic while the new source mounts.
      this.pipHasVideo = true;
      this.pipProgressAt = performance.now();
      this.pipLastTime = -1;
    }
    if (changed.has("muted")) this.applyMuted();
    if ((changed.has("pipOn") || changed.has("pipHasVideo")) && this.pipEl) {
      // Must match the render binding below, or whichever ran last wins.
      this.pipEl.hidden = !this.pipOn || !this.pipHasVideo;
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.tearDown();
  }

  private mountStreams(): void {
    if (!this.loggerId) return;
    // We always create two <video> tags so they can swap roles without remounting.
    // Audio is always pinned to the FRONT element.
    const onStall = () => {
      // Reset the connecting timer — hls.js handled a reload.
      this.stallSince = performance.now();
    };
    if (this.vPrimary && this.vPip) {
      // We mount HLS handles against the *front* and *rear* concept, not the
      // current primary/pip role. Roles flip via .applyPrimary() (swapping
      // sources between the two video elements).
      const frontVid = this.primary === "front" ? this.vPrimary : this.vPip;
      const rearVid = this.primary === "rear" ? this.vPrimary : this.vPip;
      this.hlsFront = attachHls(frontVid, {
        url: hlsUrl(this.loggerId, "front"),
        onStallReload: onStall,
      });
      this.hlsRear = attachHls(rearVid, {
        url: hlsUrl(this.loggerId, "rear"),
        onStallReload: onStall,
      });
      this.applyMuted();
      // The markup carries `autoplay`, but that only covers the very first
      // load. Anything that re-attaches media — a camera swap, or hls.js
      // rebuilding itself after a fatal error — can leave the element paused
      // with nothing to start it again.
      this.kickPlayback();
    }
  }

  /** Make sure both elements are actually playing.
   *
   *  There is no pause control in this player: clicking the video swaps
   *  cameras, so a paused element is never something the viewer asked for. It
   *  is always a stuck state, and it looked exactly like a broken stream —
   *  hls.js keeps sliding the buffer to the live edge, so currentTime jumps
   *  forward every few seconds and paints one new still frame each time, while
   *  playback itself never runs. The client reported the picture "updating
   *  once every few seconds", and the stall watchdog, seeing no continuous
   *  progress, put "no live stream" over a perfectly good feed. */
  private kickPlayback(): void {
    for (const v of [this.vPrimary, this.vPip]) {
      if (v && v.paused) v.play().catch(() => { /* blocked until a gesture */ });
    }
  }

  private applyPrimary(): void {
    // Move the two HLS streams between the primary and pip <video> elements.
    if (!this.vPrimary || !this.vPip) return;
    // Simplest approach: tear down + remount with the new role split.
    this.tearDown();
    this.mountStreams();
  }

  private applyMuted(): void {
    if (!this.vPrimary || !this.vPip) return;
    // Audio belongs to the FRONT video — find which element it's in right now.
    const isPrimaryFront = this.primary === "front";
    const frontVid = isPrimaryFront ? this.vPrimary : this.vPip;
    const rearVid = isPrimaryFront ? this.vPip : this.vPrimary;
    frontVid.muted = this.muted;
    rearVid.muted = true;
    if (!this.muted) {
      frontVid.play().catch(() => {});
    }
  }

  private tearDown(): void {
    try {
      this.hlsFront?.destroy();
    } catch {
      /* noop */
    }
    try {
      this.hlsRear?.destroy();
    } catch {
      /* noop */
    }
    this.hlsFront = null;
    this.hlsRear = null;
  }

  /* ---- Overlay watchdog: surface connecting/offline based on primary playback ---- */
  private startOverlayWatchdog(): void {
    let lastT = -1;
    let stableSince = performance.now();
    this.overlayTimer = window.setInterval(() => {
      if (!this.vPrimary) return;
      const v = this.vPrimary;
      // Self-heal a stuck element before judging whether the stream is live,
      // or we would report a fault we could simply have fixed.
      if (v.paused && v.readyState >= 2) this.kickPlayback();
      const t = v.currentTime;
      const progressing = t !== lastT && !v.paused && v.readyState >= 2;
      lastT = t;
      if (progressing) {
        stableSince = performance.now();
        if (this.overlayState !== "hidden") this.overlayState = "hidden";
        return;
      }
      const stalled = performance.now() - stableSince;
      if (stalled > OFFLINE_MSG_MS) this.overlayState = "offline";
      else if (stalled > CONNECTING_DELAY_MS) this.overlayState = "connecting";
    }, 500);
    this.startPipWatchdog();
  }

  /** Hide the inset when its camera has nothing to show.
   *
   *  A car streaming a single camera — either because the logger is set to one,
   *  or because the connection cannot carry two — left a black rectangle over
   *  the video. On a landscape phone that is roughly 8% of the screen spent
   *  showing nothing, on top of the HUD. The element already knows: a feed
   *  with no data reports readyState 0 and videoWidth 0.
   *
   *  Rides the same 500ms timer as the overlay watchdog rather than adding a
   *  second one. Note this tracks the INSET, not the rear camera specifically:
   *  after a swap the inset holds the front, and the same reasoning applies. */
  private startPipWatchdog(): void {
    this.pipProgressAt = performance.now();
    window.setInterval(() => {
      const v = this.vPip;
      if (!v) return;
      const t = v.currentTime;
      const advancing =
        t !== this.pipLastTime && v.readyState >= 2 && v.videoWidth > 0;
      this.pipLastTime = t;
      if (advancing) {
        this.pipProgressAt = performance.now();
        if (!this.pipHasVideo) this.pipHasVideo = true; // restore immediately
        return;
      }
      if (
        this.pipHasVideo &&
        performance.now() - this.pipProgressAt > PIP_HIDE_AFTER_MS
      ) {
        this.pipHasVideo = false;
      }
    }, 500);
  }

  /* ---- Public commands (called by keyboard handler in <rk-player>) ---- */
  swapCamera(): void {
    // Refuse to swap into a camera that has no picture.
    //
    // A tap anywhere on the main video swaps, which was harmless while the
    // inset was always visible — you could see where you had landed and tap it
    // back. Once the inset hides itself for a camera that is not being sent,
    // that same tap lands on empty video, promotes the dead camera to main and
    // leaves a blank screen with nothing left to tap. The client hit exactly
    // this on both phone and desktop.
    //
    // Checking the element rather than the pipHasVideo flag: that flag starts
    // optimistically true so startup does not flash, which would leave a
    // twenty second window where this guard did not hold.
    const inset = this.vPip;
    const insetHasPicture =
      !!inset && inset.readyState >= 2 && inset.videoWidth > 0;
    if (!insetHasPicture) return;

    this.primary = this.primary === "front" ? "rear" : "front";
    // Belt-and-suspenders: force a re-render in case the @property setter
    // didn't kick the Lit update cycle (we saw badges not updating during
    // testing on some builds).
    this.requestUpdate();
  }

  togglePip(): void {
    this.pipOn = !this.pipOn;
  }

  toggleMute(): void {
    this.muted = !this.muted;
    // Notify <rk-player> so its mirrored state (and the desktop bottom-bar
    // mute button's [active] indicator) stays in sync no matter which
    // control toggled — the in-video button, the M key, or the bar button.
    this.dispatchEvent(
      new CustomEvent("mutechange", { detail: this.muted, bubbles: true, composed: true })
    );
  }

  // Click handler for the in-video mute button. stopPropagation is required:
  // a bare tap on the video swaps cameras (onPrimaryClick), and on mobile the
  // button overlaps that tap area.
  private onMuteClick = (ev: Event) => {
    ev.stopPropagation();
    this.toggleMute();
  };

  /** Wall-clock PDT (ms) of the video frame currently rendering on the
   *  FRONT camera — the canonical timing source for embedded telemetry.
   *  TelemetryClient calls this each tick to pick the DATERANGE tag whose
   *  START-DATE matches the playing segment (Step L4 frame-accurate sync).
   *  Returns null if no fragment metadata is loaded yet. */
  getCurrentPdt(): number | null {
    return this.hlsFront?.getCurrentPdt() ?? null;
  }

  /** In-band ID3 telemetry payload (JSON string) for the frame currently
   *  rendering on the FRONT camera — the cam whose segments carry the muxed
   *  audio + ID3. Used by TelemetryClient in the "hlsInbandId3" source.
   *  Returns null if no in-band sample has played yet. */
  getInbandTelemetry(): string | null {
    return this.hlsFront?.getInbandTelemetry() ?? null;
  }

  async toggleFullscreen(): Promise<void> {
    // Fullscreen the *stage* (our parent), not just <rk-video>. The HUD
    // lives as a sibling of <rk-video> inside the stage; if we fullscreen
    // ourselves, the HUD disappears because it's outside the fullscreen
    // element. The stage wraps both, so both survive.
    const host =
      (this.parentElement as HTMLElement | null) ??
      (this as unknown as HTMLElement);
    const doc: any = document;
    if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
      try {
        await ((host as any).requestFullscreen?.() ??
          (host as any).webkitRequestFullscreen?.());
      } catch {
        // iOS Safari: doesn't support Element.requestFullscreen on arbitrary
        // elements — fall back to native video fullscreen on the <video>.
        // (HUD will be hidden in this iOS edge case; same trade-off as before.)
        const v = this.vPrimary as any;
        v?.webkitEnterFullscreen?.();
      }
    } else {
      try {
        await (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
      } catch {
        /* noop */
      }
    }
  }

  /* ---- PiP drag handling ---- */
  private onPipPointerDown = (ev: PointerEvent) => {
    const pip = ev.currentTarget as HTMLDivElement;
    pip.setPointerCapture(ev.pointerId);
    const rect = pip.getBoundingClientRect();
    this.dragOff = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
    this.dragging = true;
    this.pipMoved = false;
  };
  private onPipPointerMove = (ev: PointerEvent) => {
    if (!this.dragging) return;
    const host = this.getBoundingClientRect();
    const pip = this.pipEl;
    const pipRect = pip.getBoundingClientRect();
    let x = ev.clientX - host.left - this.dragOff.x;
    let y = ev.clientY - host.top - this.dragOff.y;
    x = Math.max(8, Math.min(host.width - pipRect.width - 8, x));
    y = Math.max(8, Math.min(host.height - pipRect.height - 8, y));
    pip.style.left = `${x}px`;
    pip.style.top = `${y}px`;
    pip.style.right = "auto";
    this.pipMoved = true;
  };
  private onPipPointerUp = (ev: PointerEvent) => {
    this.dragging = false;
    (ev.currentTarget as HTMLDivElement).releasePointerCapture(ev.pointerId);
  };
  /** Single click anywhere on the primary video swaps cameras. */
  private onPrimaryClick = () => this.swapCamera();

  render() {
    return html`
      <div class="stage">
        <video
          id="video-primary"
          class="primary"
          playsinline
          autoplay
          muted
          ?muted=${this.muted}
          @click=${this.onPrimaryClick}
        ></video>

        <div class="stage-logo" aria-hidden="true">
          <img src="logo.png" alt=""/>
        </div>

        ${this.clientLogos.length
          ? html`<div
              class="client-logos"
              aria-hidden="true"
              style=${`--rk-logo-scale:${this.clientLogoScale}`}
            >
              ${this.clientLogos.map(
                (uri) =>
                  html`<img
                    src=${uri}
                    alt=""
                    @error=${(e: Event) => {
                      // A corrupt logo must never leave a broken-image icon
                      // sitting over live video.
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />`,
              )}
            </div>`
          : null}

        <div class="cam-cluster">
          <span class="primary-badge">${this.primary === "front" ? "Front" : "Rear"}</span>
          <button
            class="mute-btn"
            type="button"
            title=${this.muted ? "Unmute (M)" : "Mute (M)"}
            aria-label=${this.muted ? "Unmute" : "Mute"}
            aria-pressed=${this.muted ? "false" : "true"}
            @click=${this.onMuteClick}
          >
            ${this.muted
              ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4Z"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>`
              : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4Z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>`}
          </button>
        </div>

        <div
          class="pip"
          ?hidden=${!this.pipOn || !this.pipHasVideo}
          @pointerdown=${this.onPipPointerDown}
          @pointermove=${this.onPipPointerMove}
          @pointerup=${this.onPipPointerUp}
          @click=${(ev: MouseEvent) => {
            // Treat as a tap (swap) only if the pointer didn't move during
            // the gesture — otherwise we'd swap at the end of every drag.
            if (!this.pipMoved) this.swapCamera();
            this.pipMoved = false;
            ev.stopPropagation();
          }}
        >
          <video id="video-pip" playsinline autoplay muted></video>
          <span class="pip-badge">${this.primary === "front" ? "Rear" : "Front"}</span>
        </div>

        <rk-overlay .state=${this.overlayState}></rk-overlay>
      </div>
    `;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    "rk-video": RkVideo;
  }
}
